import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import * as microsoftTeams from '@microsoft/teams-js';

export interface TeamsSsoConnectResult {
    status: 'connected' | 'consent_required' | 'failed';
    errorMessage: string;
    ssoToken: string;
}

/**
 * One consent check against Entra with its detail (D-07). `error` is Microsoft's raw answer when there is no consent
 * ('' when the check itself failed): for classification only, never shown or logged.
 */
export interface TeamsConsentCheck {
    hasConsent: boolean;
    error: string;
}

/**
 * How a Teams authentication window settled (D-07). `ok`: the page in it called notifySuccess, with `result`;
 * otherwise `reason` is the notifyFailure text or the Teams host's own reason (e.g. "CancelledByUser"). NOT
 * authoritative: under COOP the host reports "CancelledByUser" even after a success (see the class summary).
 */
export interface TeamsPopupResult {
    ok: boolean;
    result: string;
    reason: string;
}

interface HandleErrorResponse {
    isError: boolean;
    errorMessage: string;
    // ABP AjaxResponse envelope fields — present when the backend wraps the
    // payload ({ result, success, __abp }); see _unwrapAbpEnvelope.
    __abp?: boolean;
    result?: HandleErrorResponse;
}

interface CheckConsentResponse {
    hasConsent: boolean;
    needsConsent?: boolean;
    error?: string;
    __abp?: boolean;
    result?: CheckConsentResponse;
}

/**
 * Popup-free Microsoft 365 connect for the Teams client.
 *
 * The legacy Teams connect (authentication.authenticate() popup + notifySuccess
 * from the OAuth callback page) is unreliable: login.microsoftonline.com sends
 * Cross-Origin-Opener-Policy: same-origin, which severs window.opener on the
 * redirect back to our callback, so teams-js there can never reach the Teams
 * host and the host reports "CancelledByUser" even after a successful sign-in
 * or consent.
 *
 * This service avoids that channel entirely:
 *  - connect: Teams SSO token (getAuthToken — host-side, no popup) exchanged
 *    server-side via the On-Behalf-Of flow and stored by
 *    /api/WebHook/ConnectViaTeamsSso.
 *  - admin consent: still needs the interactive popup, but the popup result is
 *    best-effort only — callers must verify the real outcome against Entra with
 *    pollForConsent() (backed by /api/WebHook/CheckTeamsConsent).
 *
 * Used by the legacy connect surfaces (mine, view/add project, template wizard,
 * edit contact/company, mobile My Day, old welcome) and, through
 * M365TeamsConsentAdapter (m365-teams-consent.adapter.ts, D-07), by the
 * onboarding / Intelligence consent flow inside the Teams tab. The POSTs stay on
 * this root HttpClient (AbpHttpInterceptor: the bearer header), and the `state`
 * names the signed-in user (sign-in hotfix contract).
 */
@Injectable({ providedIn: 'root' })
export class TeamsM365ConnectService {

    private static readonly _formHeaders = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    constructor(private _http: HttpClient) {}

    getSsoToken(): Promise<string> {
        return microsoftTeams.authentication.getAuthToken();
    }

    // AADSTS90094 (admin approval required) is a consent error too (D-07): the
    // legacy Teams flows then show the admin-consent modal instead of the
    // generic legacy popup fallback.
    isConsentError(message: string): boolean {
        let lower = '';
        if (!message) { return false; }
        lower = message.toLowerCase();
        return lower.indexOf('consent_required') >= 0
            || lower.indexOf('admin_consent_required') >= 0
            || lower.indexOf('aadsts65001') >= 0
            || lower.indexOf('aadsts90094') >= 0;
    }

    // wids contains directory role template IDs
    // 62e90394-69f5-4237-9190-012177145e10 = Global Administrator
    // 29232cdf-9323-42fd-ade2-1d097af3e4de = Company Administrator
    isAzureAdAdmin(ssoToken: string): boolean {
        const adminRoles = ['62e90394-69f5-4237-9190-012177145e10', '29232cdf-9323-42fd-ade2-1d097af3e4de'];
        try {
            const parts = ssoToken.split('.');
            const payload = JSON.parse(atob(parts[1]));
            if (payload.wids && Array.isArray(payload.wids)) {
                return payload.wids.some((wid: string) => adminRoles.indexOf(wid) >= 0);
            }
            return false;
        } catch (e) {
            return false;
        }
    }

    /**
     * Connect the current user's M365 account without a popup: SSO token + OBO
     * exchange on the backend, which also stores the token set. Never rejects.
     *
     * @param state the connect `state` ("userId,tenantId,integrationId[,nonce]",
     *   built from the session ids). The onboarding consent flow passes its
     *   attempt's state (D-07); without it the legacy
     *   AppConsts.O365QueryParams.state is sent, as before.
     */
    async connectViaSso(state?: string): Promise<TeamsSsoConnectResult> {
        let ssoToken = '';
        let body: HttpParams = new HttpParams();
        let data: HandleErrorResponse = null;
        let errorMessage = '';
        let status: 'connected' | 'consent_required' | 'failed' = 'failed';
        const connectState: string =
            typeof state === 'string' && state !== '' ? state : AppConsts.O365QueryParams.state;

        try {
            ssoToken = await this.getSsoToken();
        } catch (e) {
            return { status: 'failed', errorMessage: 'Teams SSO token unavailable', ssoToken: '' };
        }
        body = body.set('ssoToken', ssoToken);
        body = body.set('state', connectState);
        try {
            data = await this._http.post<HandleErrorResponse>(
                AppConsts.remoteServiceBaseUrl + '/api/WebHook/ConnectViaTeamsSso/ConnectViaTeamsSso',
                body.toString(),
                { headers: TeamsM365ConnectService._formHeaders }
            ).toPromise();
        } catch (e) {
            return { status: 'failed', errorMessage: (e && e.message) || 'Connect request failed', ssoToken: ssoToken };
        }
        data = TeamsM365ConnectService._unwrapAbpEnvelope(data);
        if (data && !data.isError) {
            console.info('[TeamsM365Connect r5] connect status: connected');
            return { status: 'connected', errorMessage: '', ssoToken: ssoToken };
        }
        errorMessage = (data && data.errorMessage) || '';
        status = this.isConsentError(errorMessage) ? 'consent_required' : 'failed';
        // Version-stamped flow trace — a Teams webview can keep serving a stale
        // service-worker-cached bundle after a deploy; this line missing from the
        // console after a connect click means an old bundle is running.
        // r5 = D-07 (onboarding Teams adapter, state parameter, AADSTS90094).
        console.info('[TeamsM365Connect r5] connect status: ' + status);
        return {
            status: status,
            errorMessage: errorMessage,
            ssoToken: ssoToken
        };
    }

    /** One consent probe against Entra (SSO token + backend OBO check). */
    async checkConsent(): Promise<boolean> {
        const check: TeamsConsentCheck = await this.checkConsentDetail();

        return check.hasConsent;
    }

    /**
     * One consent probe against Entra with its detail (D-07): read-only (the
     * token is not stored). Never rejects: a failed check answers no consent
     * and ''.
     */
    async checkConsentDetail(): Promise<TeamsConsentCheck> {
        let ssoToken = '';
        let body: HttpParams = new HttpParams();
        let data: CheckConsentResponse = null;

        try {
            ssoToken = await this.getSsoToken();
            body = body.set('ssoToken', ssoToken);
            data = await this._http.post<CheckConsentResponse>(
                AppConsts.remoteServiceBaseUrl + '/api/WebHook/CheckTeamsConsent/CheckTeamsConsent',
                body.toString(),
                { headers: TeamsM365ConnectService._formHeaders }
            ).toPromise();
            data = TeamsM365ConnectService._unwrapAbpEnvelope(data);
            return {
                hasConsent: !!(data && data.hasConsent),
                error: data && typeof data.error === 'string' ? data.error : ''
            };
        } catch (e) {
            return { hasConsent: false, error: '' };
        }
    }

    /**
     * ABP's result filter wraps MVC controller JSON in an AjaxResponse envelope
     * ({ result, success, __abp: true }) — reading isError/hasConsent off the
     * envelope silently misreads every response (a wrapped failure has no
     * top-level isError, so it looked "connected"). Unwrap when present; raw
     * responses pass through untouched.
     */
    private static _unwrapAbpEnvelope<T extends { __abp?: boolean; result?: T }>(data: T): T {
        if (data && data.__abp === true) {
            return data.result || null;
        }
        return data;
    }

    /**
     * Re-check admin consent until Entra reports it granted or attempts run out.
     * A just-granted consent can take a while to propagate — for a freshly
     * created enterprise app (first-ever consent, or re-consent after the app
     * was removed from the tenant) propagation regularly exceeds a minute, so
     * the window here is ~100s. Callers should still treat a false result as
     * "not yet", not "never" — the connect attempt itself is the final arbiter.
     */
    async pollForConsent(maxAttempts: number = 20, intervalMs: number = 4000): Promise<boolean> {
        let attempt = 0;
        let hasConsent = false;

        for (attempt = 0; attempt < maxAttempts; attempt++) {
            hasConsent = await this.checkConsent();
            if (hasConsent) { return true; }
            if (attempt < maxAttempts - 1) { await this._delay(intervalMs); }
        }
        return false;
    }

    /**
     * Open the admin-consent popup through the own-domain intermediary page
     * (Teams requires the popup URL to be inside the app's validDomains —
     * passing login.microsoftonline.com directly is not supported).
     * Resolves when the popup settles. The outcome is NOT authoritative: under
     * COOP the callback's notifySuccess never reaches the host and Teams reports
     * "CancelledByUser" even after the admin approved — always verify with
     * pollForConsent() afterwards.
     */
    openAdminConsentPopup(): Promise<void> {
        // IMPORTANT: only call this from a direct user click (e.g. the modal's
        // "I am admin" button). authenticate() invoked from an async continuation
        // has no user gesture and the Teams host can silently refuse to open the
        // popup, killing the flow with no UI.
        const popup: Promise<TeamsPopupResult> | null = this.openAdminConsentPopupResult();

        // authenticate() can throw synchronously (e.g. SDK not initialized) —
        // treat it as "popup settled" so callers proceed to verification.
        return popup ? popup.then(() => undefined) : Promise.resolve();
    }

    /**
     * openAdminConsentPopup() with how the window settled (D-07). Opens
     * SYNCHRONOUSLY: call it from a direct user click. Null when the Teams SDK
     * refused at once (it threw, e.g. not initialized): nothing opened. The
     * answer never rejects and is NOT authoritative — verify with
     * pollForConsent().
     */
    openAdminConsentPopupResult(): Promise<TeamsPopupResult> | null {
        return this._openAuthWindow('/account/admin-consent-start', 600, 700);
    }

    /**
     * The legacy Teams consent window (/welcome/consent-start, which redirects to
     * Microsoft's authorize page; /welcome/consent-end calls notifySuccess with
     * the authorization code) for the D-07 adapter. Opens SYNCHRONOUSLY: from a
     * click, or from an async continuation knowing the Teams host may refuse it.
     * Null when the Teams SDK refused at once. The answer never rejects and is
     * NOT authoritative (COOP) — the D-07 adapter re-checks with connectViaSso().
     */
    openConsentPopup(): Promise<TeamsPopupResult> | null {
        return this._openAuthWindow('/welcome/consent-start', 600, 535);
    }

    /**
     * Opens a link from the Teams tab through the Teams host (app.openLink), e.g.
     * the admin-approval mailto: draft (D-07): the sandboxed tab iframe blocks a
     * plain mailto: navigation. When the host refuses, the link opens in a new
     * window (the mailto fallback, as contact-support.ts). Call it from the
     * click. Answers whether the Teams host took the link; never rejects.
     */
    openLink(url: string): Promise<boolean> {
        try {
            return microsoftTeams.app.openLink(url).then(
                () => true,
                () => {
                    TeamsM365ConnectService._openWindow(url);
                    return false;
                }
            );
        } catch (e) {
            TeamsM365ConnectService._openWindow(url);
            return Promise.resolve(false);
        }
    }

    /**
     * A Teams authentication window on an own-domain path (Teams opens only URLs
     * inside the app's validDomains, never login.microsoftonline.com directly),
     * opened synchronously. Null when authenticate() threw; the answer never
     * rejects.
     */
    private _openAuthWindow(path: string, width: number, height: number): Promise<TeamsPopupResult> | null {
        let pending: Promise<string>;

        try {
            pending = microsoftTeams.authentication.authenticate({
                url: window.location.origin + path,
                width: width,
                height: height
            });
        } catch (e) {
            return null;
        }
        return pending.then(
            (result: string): TeamsPopupResult => ({
                ok: true,
                result: typeof result === 'string' ? result : '',
                reason: ''
            }),
            (error: unknown): TeamsPopupResult => ({
                ok: false,
                result: '',
                reason: TeamsM365ConnectService._reasonOf(error)
            })
        );
    }

    /** A rejection's text (teams-js rejects with an Error whose message is the reason). */
    private static _reasonOf(error: unknown): string {
        if (error instanceof Error) {
            return error.message || '';
        }
        return typeof error === 'string' ? error : '';
    }

    private static _openWindow(url: string): void {
        try {
            window.open(url, '_blank');
        } catch (e) {
            // Blocked: nothing else to try.
        }
    }

    private _delay(ms: number): Promise<void> {
        return new Promise<void>((resolve) => setTimeout(resolve, ms));
    }
}
